import { createInvalidatingMutationHookFrom, createQueryHook } from '@/utils/hooks';
import {
    createOptionPreset,
    deleteOptionPreset,
    getOptionPresets,
    reorderOptionPresets,
    updateOptionPreset,
} from './option-preset.api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OptionPresetCreateOptions, OptionPresetEditOptions } from './option-preset.types';

export const useOptionPresets = (presetGroupId?: number) => {
    return useQuery({
        queryKey: ['getOptionPresets', presetGroupId],
        queryFn: () => getOptionPresets(presetGroupId!),
        enabled: !!presetGroupId,
    });
};

export const useReorderOptionPresets = () => {
    return useMutation({
        mutationFn: reorderOptionPresets,
    });
};

export const useOptionPresetUpdate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (options: OptionPresetEditOptions) => updateOptionPreset(options),
        onSuccess: ({ optionPresetGroupId }) => {
            queryClient.invalidateQueries({ queryKey: ['getOptionPresets', optionPresetGroupId] });
        },
    });
};

export const useOptionPresetCreate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (options: OptionPresetCreateOptions) => createOptionPreset(options),
        onSuccess: ({ optionPresetGroupId }) => {
            queryClient.invalidateQueries({ queryKey: ['getOptionPresets', optionPresetGroupId] });
        },
    });
};

export const useOptionPresetDelete = (presetGroupId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteOptionPreset,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getOptionPresets', presetGroupId] });
        },
    });
};

export const useOptionPresetMutations = () => {
    return {
        create: useOptionPresetCreate(),
        update: useOptionPresetUpdate(),
    };
};
